import i18n from "i18next";
import React from "react";
// findDOMNode removed in React 19, using ref instead
import ReactPlayer from "react-player";
import screenfull from "screenfull";
import MarkEditor from "./../../../_editor/components/rich_plugins/mark_editor/MarkEditor";
import Mark from "../../../common/components/mark/Mark";
import { convertHMStoSeconds } from "../../../common/common_tools";
import img from "./../../../dist/images/broken_link.png";
import { pad } from "../../../common/common_tools";
import {
  setMediaDuration,
  getMediaDuration,
} from "../../../_editor/components/timeline/IdeaTimeline/utils/buildLayers";
import {
  createZoomCamera,
  videoLookGeometry,
  videoLookPlayerStyle,
  renderVideoLook,
  VIDEO_LOOK_WRAPPER_STYLE,
  hasInfiniteDuration,
} from "./videoZoom";
import { exportStyledVideo, downloadBlob } from "./videoExport";

/* eslint-disable react/prop-types */

function fmtTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.trunc(seconds / 60);
  const s = Math.trunc(seconds % 60);
  return m + ":" + pad(s);
}

export default class EnrichedPlayerPluginEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      volume: typeof props.state?.volume === "number" ? props.state.volume : 1,
      duration: 0,
      played: 0,
      playedSeconds: 0,
      seeking: false,
      fullscreen: false,
      // controls: this.props.state.controls || true,
    };
    this._onTimelineYoutubeSync = this._onTimelineYoutubeSync.bind(this);
  }

  componentDidMount() {
    document.addEventListener("aidea-timeline-youtube-sync", this._onTimelineYoutubeSync);
    this._startZoomCamera();
  }

  // Auto-zoom camera for imported screen recordings — see videoZoom.js. Drives a
  // CSS transform on the raw <video> from state.zoomKeyframes, synced to the
  // clip's own currentTime. Shared with the visor renderer.
  _startZoomCamera() {
    const kfs = this.props.state && this.props.state.zoomKeyframes;
    if (!Array.isArray(kfs) || kfs.length === 0) return;
    if (this._zoomCam) return;
    const boxId = this.props.props && this.props.props.id;
    this._zoomCam = createZoomCamera(
      () => (boxId ? document.querySelector(`#box-${boxId} video`) : null),
      kfs
    );
    this._zoomCam.start();
  }

  // Keep the local slider value in sync when volume changes from outside the
  // player (e.g. the sound on/off toggle in the top pill's options popover).
  componentDidUpdate(prevProps) {
    const next = this.props.state?.volume;
    if (
      typeof next === "number" &&
      next !== prevProps.state?.volume &&
      next !== this.state.volume
    ) {
      this.setState({ volume: next });
    }
  }

  // ── "Ayarlarla indir": bake the recorder look + auto-zoom into a WebM ──────
  // Only offered on imported screen recordings (boxes that carry a look or a
  // zoom path) — see videoExport.js. Real-time render; Esc-free cancel via
  // the same button.
  _hasRecorderLook() {
    const st = this.props.state || {};
    return !!(
      st.videoWallpaper ||
      st.videoFrame ||
      (Array.isArray(st.zoomKeyframes) && st.zoomKeyframes.length)
    );
  }

  async _downloadStyled() {
    if (this._exportAbort) {
      this._exportAbort.abort();
      return;
    }
    const ctrl = new AbortController();
    this._exportAbort = ctrl;
    this.setState({ exportProgress: 0, exportError: null });
    try {
      const blob = await exportStyledVideo({
        url: this.props.state.url,
        state: this.props.state,
        includeAudio: this.state.exportAudio !== false,
        signal: ctrl.signal,
        onProgress: (p) => {
          if (!this._unmounted) this.setState({ exportProgress: p });
        },
      });
      const boxId = (this.props.props && this.props.props.id) || "video";
      downloadBlob(blob, `video-${boxId}.webm`);
    } catch (err) {
      if (!(err && err.name === "AbortError")) {
        // eslint-disable-next-line no-console
        console.warn("[EnrichedPlayer] styled export failed:", err);
        if (!this._unmounted) this.setState({ exportError: true });
        setTimeout(() => {
          if (!this._unmounted) this.setState({ exportError: null });
        }, 4000);
      }
    } finally {
      this._exportAbort = null;
      if (!this._unmounted) this.setState({ exportProgress: null });
    }
  }

  _renderDownloadButton() {
    if (!this._hasRecorderLook() || !this.props.state.url) return null;
    const busy = this.state.exportProgress != null;
    const label = busy
      ? i18n.t("EnrichedPlayer.DownloadStyledProgress", {
          p: Math.round(this.state.exportProgress * 100),
        })
      : this.state.exportError
        ? i18n.t("EnrichedPlayer.DownloadStyledFailed")
        : i18n.t("EnrichedPlayer.DownloadStyled");
    const withAudio = this.state.exportAudio !== false;
    const stop = (e) => e.stopPropagation();
    return (
      <div
        className={
          "enriched-player-download" +
          (busy ? " enriched-player-download--busy" : "") +
          (this.state.exportError ? " enriched-player-download--error" : "")
        }
        style={{ pointerEvents: "all" }}
        onMouseDown={stop}
      >
        {/* Include / drop the clip's own audio in the download. */}
        <button
          type="button"
          className="enriched-player-download__sound"
          disabled={busy}
          aria-pressed={withAudio}
          title={i18n.t(
            withAudio ? "EnrichedPlayer.DownloadWithSound" : "EnrichedPlayer.DownloadMuted"
          )}
          onClick={(e) => {
            stop(e);
            this.setState({ exportAudio: !withAudio });
          }}
        >
          <i className="material-icons">{withAudio ? "volume_up" : "volume_off"}</i>
        </button>
        <button
          type="button"
          className="enriched-player-download__go"
          title={busy ? i18n.t("EnrichedPlayer.DownloadStyledCancel") : label}
          onClick={(e) => {
            stop(e);
            this._downloadStyled();
          }}
        >
          <i className="material-icons">{busy ? "close" : "download"}</i>
          <span>{label}</span>
        </button>
      </div>
    );
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this._exportAbort) this._exportAbort.abort();
    document.removeEventListener("aidea-timeline-youtube-sync", this._onTimelineYoutubeSync);
    if (this._zoomCam) {
      this._zoomCam.stop();
      this._zoomCam = null;
    }
    if (this._resizeTarget) {
      this._resizeTarget.removeEventListener("resize", this._onVideoResize);
      this._resizeTarget = null;
    }
    // Pause before React detaches the <video> so its audio doesn't linger in the
    // background after the box leaves the scene (the timeline plays it imperatively).
    try {
      const boxId = this.props.props && this.props.props.id;
      const el = boxId && document.querySelector(`#box-${boxId} video`);
      if (el && typeof el.pause === "function") el.pause();
    } catch (e) {
      /* ignore */
    }
  }

  // Timeline preview driver for YouTube. A YouTube embed is an <iframe>, so the
  // timeline can't reach it the way it imperatively drives a <video>; it sends
  // this event instead. Fires every preview frame, so only act on real changes.
  _onTimelineYoutubeSync(evt) {
    const detail = (evt && evt.detail) || {};
    if (detail.boxId !== (this.props.props && this.props.props.id)) return;

    this._applyYoutubeRate(detail.rate);

    const playing = !!detail.playing;
    if (!!this.state.playing === playing) return;

    // Rewind on the play→stop edge only, so the next preview run starts at the
    // clip's beginning instead of wherever the iframe was left. No per-frame
    // drift correction: the preview rate is capped at what YouTube supports, so
    // the video and the playhead stay together on their own.
    if (!playing && typeof detail.resetSec === "number" && this.player) {
      try {
        this.player.seekTo(detail.resetSec, "seconds");
      } catch (e) {
        /* iframe not ready — nothing to rewind */
      }
    }

    this.setState({ playing });
  }

  // YouTube only accepts the rates it advertises. The timeline already caps the
  // preview at YOUTUBE_MAX_SPEED for scenes holding one, so this is a guard for
  // anything that slips past (an unusual rate list, a rate set before the cap).
  _applyYoutubeRate(rate) {
    if (typeof rate !== "number" || rate <= 0) return;
    const internal =
      this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
    if (!internal || typeof internal.setPlaybackRate !== "function") return;
    let target = rate;
    if (typeof internal.getAvailablePlaybackRates === "function") {
      const available = internal.getAvailablePlaybackRates() || [];
      if (available.length) {
        target = available.reduce((best, r) => (r <= rate && r > best ? r : best), available[0]);
      }
    }
    if (this._appliedRate === target) return;
    this._appliedRate = target;
    try {
      internal.setPlaybackRate(target);
    } catch (e) {
      /* ignore */
    }
  }

  playPause() {
    this.setState({ playing: !this.state.playing });
  }

  onClickFullscreen() {
    if (!this.state.fullscreen) {
      screenfull.request(this.player_wrapper);
    } else {
      screenfull.exit();
    }
    this.setState({ fullscreen: !this.state.fullscreen });
  }

  setVolume(e) {
    this.setState({ volume: parseFloat(e.target.value) });
  }

  // Persist the chosen volume to the plugin state (Redux) so the Visor plays the
  // video at the same level. Committed on release to avoid flooding undo history
  // with every intermediate drag value.
  commitVolume() {
    if (typeof this.props.props?.update === "function") {
      this.props.props.update("volume", this.state.volume);
    }
  }

  setPlaybackRate(e) {
    this.setState({ playbackRate: parseFloat(e.target.value) });
  }

  onSeekMouseDown() {
    this.setState({ seeking: true });
  }

  onSeekChange(e) {
    this.setState({
      played:
        (e.clientX - e.target.getBoundingClientRect().left) /
        e.target.getBoundingClientRect().width,
    });
  }

  onSeekMouseUp(e) {
    const cn = typeof e.target.className === "string" ? e.target.className : "";
    if (
      cn.indexOf("progress-player-input") !== -1 ||
      cn.indexOf("fakeProgress") !== -1 ||
      cn.indexOf("mainSlider") !== -1
    ) {
      const rect = e.target.getBoundingClientRect();
      if (!rect.width) return;
      const pos = (e.clientX - rect.left) / rect.width;
      if (!Number.isFinite(pos) || pos < 0 || pos > 1) return;

      // Header-less WebM: react-player would scale `pos` by its wrong
      // duration (the seekable sliver) — seek in seconds from the real one.
      if (hasInfiniteDuration(this.player) && this.state.duration > 0) {
        try {
          this.player.seekTo(pos * this.state.duration, "seconds");
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[EnrichedPlayerEditor] seekTo failed:", err && err.message);
        }
        this.setState({ seeking: false, played: pos });
        return;
      }

      const dur =
        (this.player &&
          typeof this.player.getDuration === "function" &&
          this.player.getDuration()) ||
        null;
      if (dur != null && !Number.isFinite(dur)) {
        this.setState({ seeking: false, played: pos });
        return;
      }

      try {
        this.player.seekTo(pos);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[EnrichedPlayerEditor] seekTo failed:", err && err.message);
      }
      this.setState({ seeking: false, played: pos });
    }
  }

  onProgress(state) {
    // We only want to update time slider if we are not currently seeking
    if (!this.state.seeking) {
      // Header-less WebM: react-player's `played` divides by its wrong
      // duration (the seekable sliver) — recompute from the resolved length.
      if (hasInfiniteDuration(this.player) && this.state.duration > 0) {
        state = { ...state, played: Math.min(1, state.playedSeconds / this.state.duration) };
      }
      this.setState(state);
    }
  }

  getDuration() {
    return this.state.duration;
  }
  // Let listeners (e.g. the upload panel's "+" button) know the media at this
  // box finished loading (or failed) so they can stop their own spinners.
  _notifyMediaSettled() {
    const boxId = this.props.props && this.props.props.id;
    if (!boxId) return;
    document.dispatchEvent(new CustomEvent("aidea-box-media-ready", { detail: { boxId } }));
  }

  onError() {
    this.setState({ ready: true });
    this._notifyMediaSettled();
  }

  onReady(e) {
    this.setState({ ready: true });
    this._notifyMediaSettled();
    // Capture natural ratio so an auto-width box doesn't collapse.
    const internal =
      this.player && this.player.getInternalPlayer && this.player.getInternalPlayer();
    if (!internal) return;
    const readDims = () => {
      const w = internal.videoWidth;
      const h = internal.videoHeight;
      if (w && h) this.setState({ naturalRatio: w / h });
    };
    if (internal.videoWidth && internal.videoHeight) {
      readDims();
    } else if (typeof internal.addEventListener === "function") {
      internal.addEventListener("loadedmetadata", readDims, { once: true });
    }
    // Screen recordings can change resolution mid-stream (a tab capture's first
    // frame often differs from the rest) — the look's geometry must follow the
    // frames actually shown, or the window letterboxes. Look boxes only.
    if (
      (this.props.state.videoWallpaper || this.props.state.videoFrame) &&
      typeof internal.addEventListener === "function" &&
      this._resizeTarget !== internal
    ) {
      if (this._resizeTarget) this._resizeTarget.removeEventListener("resize", this._onVideoResize);
      this._onVideoResize = readDims;
      this._resizeTarget = internal;
      internal.addEventListener("resize", readDims);
    }
  }

  render() {
    let marks = this.props.props.marks || {};
    let markElements = Object.keys(marks).map((id) => {
      let secondsValue = convertHMStoSeconds(marks[id].value);
      let duration = this.state.duration;
      let value = (secondsValue * 100) / duration + "%";
      let title = marks[id].title;
      let color = marks[id].color;
      return (
        <MarkEditor
          key={id}
          style={{ left: value, position: "absolute", top: "0.3em" }}
          boxId={this.props.props.id}
          time={1.5}
          mark={id}
          marks={marks}
          onRichMarkMoved={this.props.props.onRichMarkMoved}
          state={this.props.state}
          base={this.props.base}
        >
          <div className="videoMark" style={{ background: color || "#eab700" }}>
            <Mark
              style={{ position: "relative", top: "-1.7em", left: "-0.75em" }}
              color={color || "#eab700"}
              idKey={id}
              title={title}
            />
          </div>
        </MarkEditor>
      );
    });

    // Imported screen recordings carry the recorder's look (wallpaper +
    // browser frame) — see videoZoom.js. null → plain video, as before.
    const geo = videoLookGeometry(this.props.state, this.state.naturalRatio);
    const playerEl = (
      <ReactPlayer
        ref={(player) => {
          this.player = player;
        }}
        style={geo ? videoLookPlayerStyle(geo) : { width: "100%", height: "100%" }}
        height="100%"
        width="100%"
        url={this.props.state.url}
        playing={this.state.playing}
        playsinline
        loop={!!this.props.state.loop}
        config={{ file: { attributes: { disablePictureInPicture: true } } }}
        // fileConfig={{ attributes: { poster: img } }}
        volume={this.state.volume}
        // Volume 0 is muted (autoplay policy), as in the visor player.
        muted={this.state.volume === 0}
        onPlay={() => this.setState({ playing: true })}
        onPause={() => this.setState({ playing: false })}
        onEnded={() => {
          if (this.props.state.loop) return;
          this.setState({ playing: false });
        }}
        onProgress={this.onProgress.bind(this)}
        // onMouseDown={this.onSeekMouseDown.bind(this)}
        //  onChange={this.onSeekChange.bind(this)}
        // onMouseUp={this.onSeekMouseUp.bind(this)}
        onReady={this.onReady.bind(this)}
        onError={this.onError.bind(this)}
        onDuration={(duration) => {
          if (duration === Infinity || hasInfiniteDuration(this.player)) {
            // MediaRecorder WebM (imported screen recordings) has no Duration
            // header, and react-player then reports the seekable sliver, not
            // the length — never cache that. Use the timeline probe's
            // seek-resolved value instead (see videoZoom.js).
            getMediaDuration(this.props.state.url, "video").then((d) => {
              if (d && !this._unmounted) this.setState({ duration: d });
            });
            return;
          }
          this.setState({ duration });
          // Feed the timeline's duration cache — the only measurable source
          // for URLs a <video> probe can't read (YouTube API fallback).
          setMediaDuration(this.props.state.url, duration);
        }}
      />
    );

    return (
      <div
        ref={(player_wrapper) => {
          this.player_wrapper = player_wrapper;
        }}
        style={{
          width: "100%",
          height: "100%",
          aspectRatio: this.state.naturalRatio || 16 / 9,
          pointerEvents: "none",
          ...(geo ? VIDEO_LOOK_WRAPPER_STYLE : null),
        }}
        className="enriched-player-wrapper"
        duration={this.state.duration}
      >
        {geo ? renderVideoLook(React, geo, playerEl) : playerEl}
        {this._renderDownloadButton()}
        {!this.state.ready && this.props.state.url ? (
          <div className="enriched-player-loading">
            <span className="enriched-player-loading__spinner" />
          </div>
        ) : null}
        {this.props.state.controls ? (
          <div className="player-media-controls flexControls" style={{ pointerEvents: "all" }}>
            <button className="play-player-button" onClick={this.playPause.bind(this)}>
              {this.state.playing ? (
                <i className="material-icons">pause</i>
              ) : (
                <i className="material-icons">play_arrow</i>
              )}
            </button>
            <div
              className="progress-player-input dropableRichZone"
              style={{ height: "1.7em", position: "relative", bottom: "0.3em" }}
              onMouseDown={this.onSeekMouseDown.bind(this)}
              onChange={this.onSeekChange.bind(this)}
              onMouseUp={this.onSeekMouseUp.bind(this)}
            >
              <div className="fakeProgress" />

              <div
                className="mainSlider"
                style={{ position: "absolute", left: this.state.played * 100 + "%" }}
              />
              {this.state.ready ? markElements : null}
            </div>
            <div className="durationField">
              {fmtTime(this.state.playedSeconds) + "/" + fmtTime(this.state.duration)}
            </div>
            <input
              className="volume-player-input "
              type="range"
              min={0}
              max={1}
              step="any"
              value={this.state.volume}
              onChange={this.setVolume.bind(this)}
              onPointerUp={this.commitVolume.bind(this)}
              onKeyUp={this.commitVolume.bind(this)}
            />
            <button
              className="fullscreen-player-button"
              onClick={this.onClickFullscreen.bind(this)}
            >
              {!this.state.fullscreen ? (
                <i className="material-icons">fullscreen</i>
              ) : (
                <i className="material-icons">fullscreen_exit</i>
              )}
            </button>
          </div>
        ) : (
          <div
            className="player-media-controls flexControls"
            style={{ pointerEvents: "all", visibility: "hidden" }}
          >
            <div
              className="progress-player-input dropableRichZone"
              style={{ height: "1.7em", position: "relative", bottom: "0.3em" }}
            >
              <div className="fakeProgress" />
              <div
                className="mainSlider"
                style={{ position: "absolute", left: this.state.played * 100 + "%" }}
              />
              {this.state.ready ? markElements : null}
            </div>
          </div>
        )}
      </div>
    );
  }
}
/* eslint-enable react/prop-types */
